'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search } from 'lucide-react';
import { fetchResultadosPublicos, type ResultadoPublicoApi } from '@/services/indicadores';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';

const INDICATORS_DATA = [
  { id: '1', title: 'Total de Fuerza Laboral', category: 'Actividad Económica', value: '53.481', unit: 'Personas', period: '2022', tracing: 'Corrida #12' },
  { id: '2', title: '% Cobertura Red de Agua Potable', category: 'Infraestructura', value: '74,3%', unit: '%', period: '2022', tracing: 'Corrida #12' },
  { id: '4', title: 'Tasa de Desempleo Local', category: 'Actividad Económica', value: '8,2%', unit: '%', period: '2022', tracing: 'Corrida #12' },
  { id: '5', title: 'Consumo Energía per Cápita', category: 'Servicios Públicos', value: '14.250', unit: 'kWh/hab', period: '2022', tracing: 'Corrida #12' },
  { id: '3', title: '% Días Calidad Aire Aceptable', category: 'Ambiente', value: '84,9%', unit: '%', period: '2022', tracing: 'Corrida #12' },
  { id: '6', title: 'Alumnos Matriculados Nivel Primario', category: 'Educación', value: '18.200', unit: 'Alumnos', period: '2022', tracing: 'Corrida #12' },
  { id: '7', title: 'Camas Hospitalarias Disponibles', category: 'Salud', value: '428', unit: 'Camas', period: '2022', tracing: 'Corrida #12' },
  { id: '8', title: 'Presupuesto Municipal por Habitante', category: 'Economía Municipal', value: '$ 184.200', unit: '$/hab', period: '2022', tracing: 'Corrida #12' },
];

const ALL_CATEGORIES = [
  'Todas', 'Actividad Económica', 'Infraestructura', 'Ambiente', 'Salud', 'Educación', 'Gobernanza', 'Servicios Públicos', 'Economía Municipal'
];

const ALL_PERIODS = ['Todos', '2022', '2021', '2020'];

export default function CatalogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [selectedPeriod, setSelectedPeriod] = useState('Todos');
  const [indicators, setIndicators] = useState<ResultadoPublicoApi[]>([]);
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos({ per_page: 100 })
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          if (res.length > 0) setIndicators(res);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dynamic indicators catalog', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => { active = false; };
  }, []);

  const displayIndicators = indicators.length > 0
    ? indicators.map((ind) => ({
        id: ind.codigo_interno || String(ind.corrida_id),
        title: ind.indicador,
        category: ind.categoria,
        value: typeof ind.valor_resultado === 'number'
          ? ind.valor_resultado.toLocaleString('es-AR')
          : ind.valor_resultado,
        unit: ind.unidad_simbolo || ind.unidad_medida,
        period: ind.periodo_referencia ? new Date(ind.periodo_referencia).getFullYear().toString() : '2022',
        tracing: `Corrida #${ind.corrida_id}`
      }))
    : INDICATORS_DATA;

  const displayCategories = categories.length > 0
    ? ['Todas', ...categories.map(c => c.nombre)]
    : ALL_CATEGORIES;

  const displayPeriods = indicators.length > 0
    ? ['Todos', ...Array.from(new Set(indicators.map(ind => ind.periodo_referencia ? new Date(ind.periodo_referencia).getFullYear().toString() : '2022')))]
    : ALL_PERIODS;

  const filteredIndicators = displayIndicators.filter((ind) => {
    const matchesSearch = searchTerm === '' || ind.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todas' || ind.category === selectedCategory;
    const matchesPeriod = selectedPeriod === 'Todos' || ind.period === selectedPeriod;
    return matchesSearch && matchesCategory && matchesPeriod;
  });

  return (
    <>
      {/* Filter Bar (Sub-header) */}
      <div className="bg-[#F3F4F6] border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex flex-col md:flex-row items-center gap-4 justify-between">
          
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64 md:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar indicador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002B5B] focus:border-transparent text-gray-800"
              />
            </div>

            {/* Category Select */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#002B5B]"
            >
              {displayCategories.map(cat => (
                <option key={cat} value={cat}>Categoría: {cat}</option>
              ))}
            </select>

            {/* Period Select */}
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#002B5B]"
            >
              {displayPeriods.map(period => (
                <option key={period} value={period}>Período: {period}</option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-400 font-medium whitespace-nowrap">
            {filteredIndicators.length} indicadores encontrados
          </div>

        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto w-full px-8 py-10 flex-1">
        
        <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-gray-100 overflow-hidden">
          
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-xl font-[family-name:var(--font-righteous)] text-[#002B5B] tracking-wide">
              Indicadores Publicados · Período {selectedPeriod === 'Todos' ? 'Histórico' : selectedPeriod}
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Indicador</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Categoría</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Valor Vigente</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Unidad</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Período</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100">Trazabilidad</th>
                  <th className="px-6 py-4 font-bold border-b border-gray-100"></th>
                </tr>
              </thead>
              <tbody>
                {filteredIndicators.length > 0 ? (
                  filteredIndicators.map((ind) => (
                    <tr key={ind.id} className="hover:bg-[#F8FAFC] transition-colors group">
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-sm font-bold text-[#0053D6]">{ind.title}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-400">{ind.category}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-sm font-bold text-gray-900">{ind.value}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-400">{ind.unit}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <div className="text-xs text-gray-400">{ind.period}</div>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50">
                        <span className="inline-flex px-3 py-1 bg-[#EFF6FF] text-[#3B82F6] rounded-full text-[10px] font-bold">
                          {ind.tracing}
                        </span>
                      </td>
                      <td className="px-6 py-4 border-b border-gray-50 text-right">
                        <Link 
                          href={`/indicadores/${ind.id}`} 
                          className="inline-flex items-center justify-center bg-[#F0F5FA] hover:bg-[#E2E8F0] text-[#0053D6] text-xs font-bold py-1.5 px-4 rounded-lg transition-colors"
                        >
                          Ver <span className="ml-1 opacity-60">→</span>
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400 text-sm">
                      No se encontraron indicadores con los filtros actuales.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </>
  );
}
